import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { acquireAppToken } from '@/lib/app-token';
import { authKeys } from '@/lib/query-keys';
import { ROLES, rolesFromToken, type Role } from '@/lib/user-roles';

function devRoles(): string[] {
  const configured = import.meta.env.VITE_DEV_ROLES;
  if (configured === undefined) return Object.values(ROLES);
  return configured
    .split(',')
    .map((r) => r.trim())
    .filter(Boolean);
}

export interface UseRolesReturn {
  roles: string[];
  isLoading: boolean;
  /**
   * Re-reads roles from a freshly issued token, falling back to a popup. Call
   * from a click handler — a newly granted role is not in the cached token.
   */
  recheck: () => Promise<void>;
}

/**
 * Client-side role check — UX only, not a security boundary (the campaign API
 * does not enforce the role). Resolved silently on load; a silent failure
 * reads as "no roles" until `recheck` succeeds.
 */
export function useRoles(): UseRolesReturn {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: authKeys.roles(),
    queryFn: async () => rolesFromToken(await acquireAppToken({ interactive: false })),
    initialData: import.meta.env.DEV ? devRoles : undefined,
    staleTime: Infinity,
    retry: false,
  });

  const recheck = useCallback(async () => {
    const roles = import.meta.env.DEV
      ? devRoles()
      : rolesFromToken(await acquireAppToken({ forceRefresh: true }));
    queryClient.setQueryData(authKeys.roles(), roles);
  }, [queryClient]);

  return { roles: query.data ?? [], isLoading: query.isPending, recheck };
}

export function useHasRole(role: Role): { hasRole: boolean; isLoading: boolean } {
  const { roles, isLoading } = useRoles();
  return { hasRole: roles.includes(role), isLoading };
}
